// src/components/asset/Module3DViewer.jsx
// Итерация 8.5: переиспользование moduleGeometry.js (единый источник геометрии).
//
// Что изменилось:
//   - Удалено: inline renderGeometry(), geometry useMemo, materialProps useMemo,
//     safeModule useMemo, visual extraction.
//   - Добавлено: import { getModuleGeometry, renderGeometryJSX }.
//   - ModuleModel — тонкий рендер через getModuleGeometry.
//
// Контракт props НЕ изменился: module, width, height, interactive, autoRotate,
// showInfo, onClose.
//
// ⚠️ Для старых модулей (с visual_representation) — работает как раньше
//    (приоритет 0 в moduleGeometry.js).
//    Для новых модулей (с base_form) — новое поведение (из installed_modules).

import React, { useRef, useMemo, useState, Suspense } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import {
  OrbitControls,
  PerspectiveCamera,
  Environment,
  ContactShadows,
  Html,
} from '@react-three/drei';
import {
  Box,
  Typography,
  Paper,
  IconButton,
  CircularProgress,
  Tooltip,
  Button,
} from '@mui/material';
import {
  Close as CloseIcon,
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  RestartAlt as ResetIcon,
} from '@mui/icons-material';

// 🔥 Итерация 8.5: единый источник геометрии
import { getModuleGeometry, renderGeometryJSX } from '../../utils/moduleGeometry';

// ============================================================================
// Loader
// ============================================================================
function Loader() {
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100%',
        bgcolor: '#1a1a2e',
      }}
    >
      <CircularProgress />
    </Box>
  );
}

// ============================================================================
// ModuleModel — тонкий рендер (8.5)
// ============================================================================
function ModuleModel({ module, autoRotate }) {
  const meshRef = useRef();

  // 🔥 8.5: геометрия из единого источника
  const geom = useMemo(() => getModuleGeometry(module), [module]);

  // Анимация вращения (только в редакторе)
  useFrame((state) => {
    if (autoRotate && meshRef.current) {
      meshRef.current.rotation.y += 0.005;
      meshRef.current.rotation.x = Math.sin(state.clock.elapsedTime * 0.2) * 0.1;
    }
  });

  return (
    <mesh ref={meshRef} castShadow receiveShadow>
      {renderGeometryJSX(geom)}
      <meshStandardMaterial
        color={geom.material.color}
        roughness={geom.material.roughness}
        metalness={geom.material.metalness}
        emissive={geom.material.emissive || '#000000'}
        emissiveIntensity={geom.material.emissiveIntensity || 0}
      />
    </mesh>
  );
}

// ============================================================================
// ModuleEffects (только для редактора — не в игре)
// ============================================================================
function ModuleEffects({ module }) {
  const visual = module?.visual_representation || {};
  const effects = visual.effects || [];

  if (!effects.length) return null;

  return (
    <>
      {effects.map((effect, index) => {
        if (!effect) return null;

        if (effect.type === 'plasma_glow' || effect.type === 'energy_field') {
          return (
            <mesh key={index}>
              <sphereGeometry args={[2.5, 16, 16]} />
              <meshBasicMaterial
                color={effect.color || '#00ffff'}
                transparent
                opacity={0.2}
              />
            </mesh>
          );
        }
        return null;
      })}
    </>
  );
}

// ============================================================================
// InfoPanel (вне Canvas)
// ============================================================================
function InfoPanel({ module, onClose }) {
  if (!module) return null;

  return (
    <Paper
      sx={{
        position: 'absolute',
        bottom: 16,
        left: 16,
        p: 2,
        maxWidth: 300,
        bgcolor: 'rgba(255,255,255,0.95)',
        borderRadius: 2,
        boxShadow: 3,
        zIndex: 10,
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
        <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 'bold' }}>
          {module.name || 'Module'}
        </Typography>
        {onClose && (
          <IconButton size="small" onClick={onClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        )}
      </Box>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        {module.purpose || 'Module'} • {module.category || 'standard'}
      </Typography>
      <Box sx={{ mt: 1 }}>
        <Typography variant="caption" component="div">
          <b>Размер:</b> {module.size_tier || module.slot_size || 'medium'}
        </Typography>
        <Typography variant="caption" component="div">
          <b>Tech Level:</b> {module.tech_level || 5}
        </Typography>
        <Typography variant="caption" component="div">
          <b>Масса:</b>{' '}
          {module.base_stats?.mass_kg
            ? (module.base_stats.mass_kg / 1000).toFixed(2)
            : module.mass_kg
              ? (module.mass_kg / 1000).toFixed(2)
              : '0'}{' '}
          т
        </Typography>
        <Typography variant="caption" component="div">
          <b>Энергия:</b>{' '}
          {module.base_stats?.power_draw || module.power_draw || 0} кВт
        </Typography>
        {(module.base_stats?.power_output || module.power_output) > 0 && (
          <Typography variant="caption" component="div" color="success.main">
            <b>Генерация:</b> +{module.base_stats?.power_output || module.power_output} кВт
          </Typography>
        )}
      </Box>
      {module.base_form && (
        <Box sx={{ mt: 1 }}>
          <Typography variant="caption" component="div">
            <b>Форма:</b> {module.base_form}
          </Typography>
          <Typography variant="caption" component="div">
            <b>Материал:</b>{' '}
            {module.visual?.material || module.visual_representation?.material?.type || 'standard'}
          </Typography>
        </Box>
      )}
      {module.description && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          {module.description}
        </Typography>
      )}
    </Paper>
  );
}

// ============================================================================
// Scene3D
// ============================================================================
function Scene3D({ module, autoRotate, showInfo }) {
  const controlsRef = useRef();

  return (
    <>
      <PerspectiveCamera makeDefault position={[5, 5, 5]} fov={50} />
      <OrbitControls
        ref={controlsRef}
        enablePan={true}
        enableZoom={true}
        enableRotate={true}
        zoomSpeed={1.2}
        rotateSpeed={1.0}
        target={[0, 0, 0]}
      />

      {/* Освещение */}
      <ambientLight intensity={0.6} />
      <directionalLight
        position={[10, 10, 5]}
        intensity={1}
        castShadow
        shadow-mapSize-width={1024}
        shadow-mapSize-height={1024}
      />
      <directionalLight position={[-5, 5, -5]} intensity={0.5} />
      <pointLight position={[0, 3, 0]} intensity={0.3} />
      <pointLight position={[-2, 1, -3]} intensity={0.4} color="#ffaa66" />

      {/* Сетка */}
      <gridHelper args={[20, 20, '#888888', '#444444']} position={[0, -2, 0]} />

      {/* Окружение */}
      <Environment preset="city" background={false} />

      {/* Модель */}
      <group position={[0, 0, 0]}>
        <ModuleModel module={module} autoRotate={autoRotate} />
        <ModuleEffects module={module} />
      </group>

      {/* Тени */}
      <ContactShadows
        position={[0, -2, 0]}
        opacity={0.4}
        scale={15}
        blur={2}
        far={5}
        resolution={512}
        color="#000000"
      />

      {/* Метка */}
      {showInfo && module?.name && (
        <Html
          position={[0, 3.5, 0]}
          center
          distanceFactor={10}
          style={{ pointerEvents: 'none' }}
        >
          <Paper
            sx={{
              p: 0.75,
              bgcolor: 'rgba(0,0,0,0.75)',
              color: 'white',
              borderRadius: 1,
              whiteSpace: 'nowrap',
              fontSize: '0.75rem',
              backdropFilter: 'blur(4px)',
            }}
          >
            <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
              {module.name}
            </Typography>
          </Paper>
        </Html>
      )}
    </>
  );
}

// ============================================================================
// Основной компонент
// ============================================================================
export default function Module3DViewer({
  module,
  width = '100%',
  height = '400px',
  interactive = true,
  autoRotate = false,
  showInfo = false,
  onClose,
}) {
  const [zoom, setZoom] = useState(1);
  const [error, setError] = useState(false);
  const [showInfoPanel, setShowInfoPanel] = useState(showInfo);

  // Проверка валидности модуля
  const isValidModule = useMemo(() => {
    return module && typeof module === 'object' && (module.id || module.instance_id);
  }, [module]);

  const handleResetView = () => {
    setZoom(1);
  };

  if (!isValidModule) {
    return (
      <Box
        sx={{
          width,
          height,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          bgcolor: 'background.default',
          borderRadius: 1,
        }}
      >
        <Typography color="text.secondary">Модуль не выбран</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          width,
          height,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          bgcolor: 'background.default',
          borderRadius: 1,
          flexDirection: 'column',
          gap: 2,
        }}
      >
        <Typography color="error">Ошибка загрузки 3D модели</Typography>
        <Typography variant="caption" color="text.secondary">
          {module?.name || 'Модуль'} не имеет корректного 3D представления
        </Typography>
        <Button variant="outlined" size="small" onClick={() => setError(false)}>
          Повторить
        </Button>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        position: 'relative',
        width,
        height,
        bgcolor: '#1a1a2e',
        borderRadius: 1,
        overflow: 'hidden',
      }}
    >
      {/* Controls */}
      {interactive && (
        <Box
          sx={{
            position: 'absolute',
            top: 16,
            right: 16,
            zIndex: 10,
            display: 'flex',
            gap: 1,
            bgcolor: 'rgba(0,0,0,0.6)',
            borderRadius: 2,
            p: 0.5,
            backdropFilter: 'blur(8px)',
          }}
        >
          {onClose && (
            <Tooltip title="Закрыть">
              <IconButton
                size="small"
                onClick={onClose}
                sx={{ color: 'white', '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' } }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="Приблизить">
            <IconButton
              size="small"
              sx={{ color: 'white', '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' } }}
              onClick={() => setZoom((z) => Math.min(z + 0.2, 3))}
            >
              <ZoomInIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Отдалить">
            <IconButton
              size="small"
              sx={{ color: 'white', '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' } }}
              onClick={() => setZoom((z) => Math.max(z - 0.2, 0.5))}
            >
              <ZoomOutIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Сбросить вид">
            <IconButton
              size="small"
              sx={{ color: 'white', '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' } }}
              onClick={handleResetView}
            >
              <ResetIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      )}

      {/* InfoPanel (вне Canvas) */}
      {showInfoPanel && module && (
        <InfoPanel module={module} onClose={() => setShowInfoPanel(false)} />
      )}

      {/* 3D Canvas */}
      <Suspense fallback={<Loader />}>
        <Canvas
          style={{ width: '100%', height: '100%', display: 'block' }}
          camera={{ position: [5 * zoom, 5 * zoom, 5 * zoom], fov: 50 }}
          shadows
          gl={{ antialias: true, alpha: false }}
          onCreated={({ gl }) => {
            gl.setClearColor('#1a1a2e');
          }}
          onError={(err) => {
            console.error('Canvas error:', err);
            setError(true);
          }}
        >
          <Scene3D
            module={module}
            autoRotate={autoRotate}
            showInfo={showInfo}
          />
        </Canvas>
      </Suspense>
    </Box>
  );
}
